import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import KinConstructionKit from '../components/KinConstructionKit';

const KinCreate = () => {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [canCreate, setCanCreate] = useState(false);
  const [error, setError] = useState(null);
  const isCreatingRef = useRef(false);

  useEffect(() => {
    if (!loading) {
      // Only check if we're not in the middle of creating a Kin
      if (!isCreatingRef.current) {
        checkUserStatus();
      }
    }
  }, [loading, user]);

  const checkUserStatus = async () => {
    try {
      setIsChecking(true);
      setError(null);
      
      if (!user) {
        navigate('/login');
        return;
      }

      // If user already has a Kin, redirect to dashboard
//      if (user.activeKinId) {
//        navigate('/kin-dashboard');
//        return;
//      }

      const token = localStorage.getItem('token');
      const rolesRes = await fetch('/api/user/roles', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (rolesRes.ok) {
        const rolesData = await rolesRes.json();
        const isCoordinator = rolesData.roles?.includes('kin_coordinator');
        
        if (isCoordinator) {
          navigate('/kin-dashboard');
          return;
        }
      }
      
      setCanCreate(true);
    } catch (err) {
      console.error('Error checking user status:', err);
      setError('Failed to check your eligibility. Please try again.');
    } finally {
      setIsChecking(false);
    }
  };

  const handleKinCreated = async (kinData) => {
    console.log('🎉 Kin created successfully:', kinData);
    isCreatingRef.current = true;
    
    // Refresh user data to get updated activeKinId
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Small delay to ensure state is updated
        setTimeout(() => {
          navigate('/kin-dashboard');
        }, 100);
      } else {
        // Fallback: just navigate
        navigate('/kin-dashboard');
      }
    } catch (err) {
      console.error('Failed to refresh user data:', err);
      navigate('/kin-dashboard');
    }
  };

  if (isChecking || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-gray-600">Checking your eligibility...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-green-600 text-white px-6 py-2 rounded-lg"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!canCreate) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">👥</div>
          <p className="text-gray-600 mb-4">You're already part of a Kin.</p>
          <button
            onClick={() => navigate('/kin-dashboard')}
            className="bg-green-600 text-white px-6 py-2 rounded-lg"
          >
            Go to Kin Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🏗️</span>
            <div>
              <p className="font-semibold text-blue-800">Create Your Kin</p>
              <p className="text-sm text-blue-700">
                Follow the steps below to set up your buying group. 
                You'll need to set your location, invite members, and configure your Kin's settings.
              </p>
            </div>
          </div>
        </div>
        <KinConstructionKit onComplete={handleKinCreated} />
      </div>
    </div>
  );
};

export default KinCreate;
